if (typeof ProductCardVariantAvailability !== 'function') {

    /**
     * Product Card Variant Availability Component
     * 
     * Displays availability information for pickup, shipping, and local delivery
     * based on variant data, product tags, weight restrictions, and selected location.
     * 
     * Features:
     * - Shows pickup availability based on selected store location
     * - Respects weight restrictions for shipping and local delivery
     * - Hides options based on product tags
     * - Can hide shipping in product cards via theme settings
     * - Updates dynamically when variant or location changes
     */
    class ProductCardVariantAvailability extends HTMLElement {

        constructor() {
            super();
            
            this.storeSelector = document.querySelector('store-selector[data-main-selector]');
            this.selectedStore = localStorage.getItem('selected-store');
            
            this.selectors = {
                pickupContainer: '.product-card-variant-availability__pickup',
                shippingContainer: '.product-card-variant-availability__shipping',
                localDeliveryContainer: '.product-card-variant-availability__local-delivery',
            }

            this.elements = {
                pickupContainer: this.querySelector(this.selectors.pickupContainer),
                shippingContainer: this.querySelector(this.selectors.shippingContainer),
                localDeliveryContainer: this.querySelector(this.selectors.localDeliveryContainer),
            }

            this.classes = {
                isHidden: 'is-hidden',
            }

            this.variantsAvailability = JSON.parse(this.dataset.variantData);
            this.variantId = this.dataset.variantId;
        }

        #updatePickupAvailability() {
            if (!this.elements.pickupContainer) return;
            
            // Check if no location is selected
            if (!this.selectedStore) {
                this.updateContainerText(this.elements.pickupContainer, 'No location selected', 'unavailable');
                this.elements.pickupContainer.classList.remove(this.classes.isHidden);
                return;
            }

            // Find the matching variant
            const matchingVariant = this.variantsAvailability.find(variant =>
                variant.variantId.toString() === this.variantId.toString()
            );

            if (matchingVariant) {
                // Handle untracked variants - show as available at all stores
                if (matchingVariant.availability === "untracked") {
                    const text = this.dataset.pickupAvailableText.replace('[LOCATION]', this.selectedStore?.replaceAll('-', ' '));
                    this.updateContainerText(this.elements.pickupContainer, text, 'available');
                } else {
                    // Find the matching location
                    const matchingLocation = matchingVariant.availability.find(availability => {
                        const updatedLocation = availability.location.replaceAll(' ', '-').toLowerCase();
                        return updatedLocation === this.selectedStore;
                    });

                    if (matchingLocation) {
                        const availableQty = Number(matchingLocation.available);
                        const limitedThreshold = Number(this.dataset.limitedThreshold) || 3;

                        if (availableQty > 0) {
                            if (availableQty <= limitedThreshold) {
                                const text = this.dataset.pickupLimitedText.replace('[LOCATION]', matchingLocation.location);
                                this.updateContainerText(this.elements.pickupContainer, text, 'limited');
                            } else {
                                const text = this.dataset.pickupAvailableText.replace('[LOCATION]', matchingLocation.location);
                                this.updateContainerText(this.elements.pickupContainer, text, 'available');
                            }
                        } else {
                            const text = this.dataset.pickupUnavailableText.replace('[LOCATION]', matchingLocation.location);
                            this.updateContainerText(this.elements.pickupContainer, text, 'unavailable');
                        }
                    }
                }
            }

            this.elements.pickupContainer.classList.remove(this.classes.isHidden);
        }

        #updateShippingAvailability() {
            if (!this.elements.shippingContainer) return;

            const matchingVariant = this.variantsAvailability.find(variant =>
                variant.variantId.toString() === this.variantId.toString()
            );

            if (matchingVariant) {
                const weight = Number(matchingVariant.weight);
                const shippingWeight = Number(this.dataset.shippingWeight);
                
                // Check weight restrictions
                if (shippingWeight > 0 && weight > shippingWeight) {
                    this.updateContainerText(this.elements.shippingContainer, this.dataset.shippingUnavailableText, 'unavailable');
                } else {
                    this.updateContainerText(this.elements.shippingContainer, this.dataset.shippingAvailableText, 'available');
                }
            }

            this.elements.shippingContainer.classList.remove(this.classes.isHidden);
        }

        updateLocalDeliveryAvailability() {
            if (!this.elements.localDeliveryContainer) return;

            const matchingVariant = this.variantsAvailability.find(variant =>
                variant.variantId.toString() === this.variantId.toString()
            );

            if (matchingVariant) {
                const weight = Number(matchingVariant.weight);
                const localDeliveryWeight = Number(this.dataset.localDeliveryWeight);
                
                // Check weight restrictions
                if (localDeliveryWeight > 0 && weight > localDeliveryWeight) {
                    this.updateContainerText(this.elements.localDeliveryContainer, this.dataset.localDeliveryUnavailableText, 'unavailable');
                } else {
                    this.updateContainerText(this.elements.localDeliveryContainer, this.dataset.localDeliveryAvailableText, 'available');
                }
            }

            this.elements.localDeliveryContainer.classList.remove(this.classes.isHidden);
        }

        updateContainerText(container, text, status = null) {
            // Find the existing paragraph element or create one
            let paragraph = container.querySelector('p');
            if (!paragraph) {
                paragraph = document.createElement('p');
                container.appendChild(paragraph);
            }
            
            // If no status provided, just set the text
            if (!status) {
                paragraph.textContent = text;
                return;
            }
            
            // Create colored text by wrapping status words
            let coloredText = text;
            
            if (status === 'available') {
                coloredText = text.replace(/\b(available|in stock)\b/gi, '<span class="available">Available</span>');
            } else if (status === 'limited') {
                coloredText = text.replace(/\b(limited|low stock|few left)\b/gi, '<span class="limited">Limited</span>');
            } else if (status === 'unavailable') {
                coloredText = text.replace(/\b(unavailable|out of stock|not available)\b/gi, '<span class="unavailable">Unavailable</span>');
            }
            
            paragraph.innerHTML = coloredText;
        }

        #checkTagRestrictions() {
            const productTags = this.dataset.productTags ? this.dataset.productTags.split(',') : [];
            const shippingHideTags = this.dataset.shippingHideBasedOnTag ? this.dataset.shippingHideBasedOnTag.split(',') : [];
            const localDeliveryHideTags = this.dataset.localDeliveryHideBasedOnTag ? this.dataset.localDeliveryHideBasedOnTag.split(',') : [];

            // Check shipping tag restrictions
            const hideShipping = shippingHideTags.some(tag => 
                productTags.some(productTag => productTag.trim().toLowerCase() === tag.trim().toLowerCase())
            );

            // Check local delivery tag restrictions
            const hideLocalDelivery = localDeliveryHideTags.some(tag => 
                productTags.some(productTag => productTag.trim().toLowerCase() === tag.trim().toLowerCase())
            );

            return { hideShipping, hideLocalDelivery };
        }

        updateAvailability() {
            const { hideShipping, hideLocalDelivery } = this.#checkTagRestrictions();
            const hideShippingInCards = this.dataset.hideShippingInProductCards === 'true';

            // Update pickup availability
            this.#updatePickupAvailability();

            // Update shipping availability (only if not hidden in product cards)
            if (!hideShippingInCards && !hideShipping) {
                this.#updateShippingAvailability();
            } else {
                this.elements.shippingContainer?.classList.add(this.classes.isHidden);
            }

            // Update local delivery availability
            if (!hideLocalDelivery) {
                this.updateLocalDeliveryAvailability();
            } else {
                this.elements.localDeliveryContainer?.classList.add(this.classes.isHidden);
            }
        }

        connectedCallback() {
            this.updateAvailability();

            // Listen for store changes
            if (this.storeSelector) {
                this.storeSelector.addEventListener('storechanged', () => {
                    this.selectedStore = localStorage.getItem('selected-store');
                    this.updateAvailability();
                });
            }

            // Listen for variant changes
            document.addEventListener('VARIANT_CHANGE', (e) => {
                this.variantId = e.target.currentVariant.id;
                this.updateAvailability();
            });
        }
    }

    if (typeof customElements.get('product-card-variant-availability') == 'undefined') {
        customElements.define('product-card-variant-availability', ProductCardVariantAvailability);
    }
}
